import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { css, cx } from "styled-system/css";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { cf } from "@/components/brand/sfturnos-styles";
import { DashboardPageShell } from "@/components/dashboard/dashboard-page-shell";
import { LoadingFrame } from "@/components/ui/loading-frame";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Modal } from "@/components/ui/modal";
import { useConfirm } from "@/hooks/useConfirm";

type AgendaItem = {
  id: string;
  name: string;
  slug: string;
  isPrimary: boolean;
  plan: string;
  agendasAllowed: number | null;
};

const planLabel: Record<string, string> = {
  FREE: "Free",
  PRO: "Pro",
  ENTERPRISE: "Enterprise",
};

const planAgendaLimit: Record<string, number> = {
  FREE: 1,
  PRO: 3,
  ENTERPRISE: -1,
};

export function DashboardAgendas() {
  const { refresh } = useAuth();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [agendas, setAgendas] = useState<AgendaItem[] | null>(null);
  const [activePid, setActivePid] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newSlug, setNewSlug] = useState("");
  const [createErr, setCreateErr] = useState<string | null>(null);
  const [createBusy, setCreateBusy] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [switching, setSwitching] = useState<string | null>(null);

  async function load() {
    const r = await fetchWithSessionSync("/api/dashboard/agendas", { credentials: "include" }, refresh);
    if (!r.ok) return;
    const data = (await r.json()) as { agendas: AgendaItem[]; activeProfessionalId: string | null };
    setAgendas(data.agendas);
    setActivePid(data.activeProfessionalId);
  }

  useEffect(() => {
    void load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  // Abre el modal si venimos con ?new=1 (desde el sidebar)
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setCreating(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  async function createAgenda() {
    if (!newName.trim() || !newSlug.trim()) {
      setCreateErr("Completá nombre y enlace.");
      return;
    }
    setCreateBusy(true);
    setCreateErr(null);
    const r = await fetchWithSessionSync(
      "/api/dashboard/agendas",
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newName.trim(), slug: newSlug.trim() }),
      },
      refresh,
    );
    setCreateBusy(false);
    if (!r.ok) {
      const j = (await r.json().catch(() => ({}))) as { error?: string };
      setCreateErr(j.error ?? "No se pudo crear la agenda.");
      return;
    }
    setCreating(false);
    setNewName("");
    setNewSlug("");
    await load();
  }

  async function deleteAgenda(id: string) {
    if (
      !(await confirm({
        message: "¿Eliminar esta agenda? Se eliminarán todos sus servicios, horarios y turnos.",
        tone: "danger",
        confirmLabel: "Eliminar",
      }))
    )
      return;
    setDeletingId(id);
    await fetchWithSessionSync(`/api/dashboard/agendas/${id}`, { method: "DELETE", credentials: "include" }, refresh);
    setDeletingId(null);
    await load();
  }

  async function switchTo(id: string) {
    setSwitching(id);
    await fetchWithSessionSync(`/api/dashboard/agendas/${id}/switch`, { method: "POST", credentials: "include" }, refresh);
    window.location.reload();
  }

  async function editAgenda(id: string) {
    if (id === activePid) {
      navigate("/dashboard/settings");
      return;
    }
    setSwitching(id);
    await fetchWithSessionSync(`/api/dashboard/agendas/${id}/switch`, { method: "POST", credentials: "include" }, refresh);
    window.location.href = "/dashboard/settings";
  }

  if (!agendas) {
    return (
      <DashboardPageShell eyebrow="Multi-agenda" title="Mis agendas">
        <LoadingFrame variant="light" minHeight={240} />
      </DashboardPageShell>
    );
  }

  const primaryPlan = agendas[0]?.plan ?? "FREE";
  const limit = agendas[0]?.agendasAllowed != null && primaryPlan === "ENTERPRISE"
    ? agendas[0].agendasAllowed
    : planAgendaLimit[primaryPlan] ?? 1;
  const canCreate = limit === -1 || agendas.length < limit;

  return (
    <DashboardPageShell
      eyebrow="Multi-agenda"
      title="Mis agendas"
      description="Cada agenda tiene su propia página pública de reservas, servicios y horarios independientes."
    >
      <Modal
        open={creating}
        onClose={() => {
          setCreating(false);
          setCreateErr(null);
          setNewName("");
          setNewSlug("");
        }}
        title="Nueva agenda"
      >
        <div className={css({ display: "flex", flexDir: "column", gap: "4" })}>
          {createErr ? <p className={css({ fontSize: "sm", color: "#b91c1c" })}>{createErr}</p> : null}
          <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
            <span className={css({ fontSize: "sm", color: "#64748b" })}>Nombre de la agenda</span>
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Ej: Agenda de tarde"
            />
          </label>
          <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
            <span className={css({ fontSize: "sm", color: "#64748b" })}>Enlace público (slug)</span>
            <div className={css({ display: "flex", alignItems: "center", gap: "1" })}>
              <span className={css({ fontSize: "sm", color: "#94a3b8", flexShrink: 0 })}>sfturnos.com/book/</span>
              <Input
                value={newSlug}
                onChange={(e) => setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
                placeholder="mi-agenda-2"
              />
            </div>
          </label>
          <div className={css({ display: "flex", gap: "2" })}>
            <Button variant="secondary" onClick={() => setCreating(false)}>Cancelar</Button>
            <Button
              variant="primary"
              disabled={createBusy || !newName.trim() || !newSlug.trim()}
              onClick={() => void createAgenda()}
            >
              {createBusy ? "Creando…" : "Crear agenda"}
            </Button>
          </div>
        </div>
      </Modal>

      <div className={css({ display: "flex", flexDir: "column", gap: "4", maxW: "42rem" })}>
        {/* Plan banner */}
        <div className={cx(cf.dashSurface, css({ p: "4", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "3" }))}>
          <div>
            <p className={css({ fontSize: "xs", color: "#64748b" })}>Plan actual</p>
            <p className={css({ fontWeight: "bold", color: "#0f172a" })}>{planLabel[primaryPlan] ?? primaryPlan}</p>
          </div>
          <div className={css({ textAlign: "right" })}>
            <p className={css({ fontSize: "xs", color: "#64748b" })}>
              Agendas: <strong className={css({ color: "#0f172a" })}>{agendas.length}</strong>
              {limit !== -1 ? ` / ${limit}` : " (ilimitadas)"}
            </p>
            {!canCreate && (
              <p className={css({ fontSize: "xs", color: "#b45309", mt: "1" })}>
                Límite alcanzado · <a href="/dashboard/subscription" className={css({ color: "token(colors.brand.600)", textDecoration: "underline" })}>Mejorar plan</a>
              </p>
            )}
          </div>
        </div>

        {/* Lista de agendas */}
        <ul className={css({ display: "flex", flexDir: "column", gap: "3" })}>
          {agendas.map((a) => (
            <li
              key={a.id}
              className={cx(
                cf.dashSurface,
                css({
                  p: "4",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "3",
                  borderLeftWidth: "3px",
                  borderLeftColor: a.id === activePid ? "token(colors.brand.500)" : "transparent",
                }),
              )}
            >
              <div className={css({ minW: 0 })}>
                <p className={css({ fontWeight: "semibold", color: "#0f172a" })}>
                  {a.name}
                  {a.isPrimary && (
                    <span className={css({ ml: "2", fontSize: "xs", color: "#64748b", fontWeight: "normal" })}>
                      (principal)
                    </span>
                  )}
                  {a.id === activePid && (
                    <span className={css({ ml: "2", fontSize: "xs", color: "token(colors.brand.600)", fontWeight: "semibold" })}>
                      activa
                    </span>
                  )}
                </p>
                <p className={css({ fontSize: "sm", color: "#64748b", mt: "0.5" })}>
                  <a
                    href={`https://www.sfturnos.com/book/${a.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={css({ color: "token(colors.brand.600)", textDecoration: "underline" })}
                  >
                    sfturnos.com/book/{a.slug}
                  </a>
                </p>
              </div>
              <div className={css({ display: "flex", gap: "2", flexShrink: 0 })}>
                <Button
                  variant="secondary"
                  disabled={switching === a.id}
                  onClick={() => void editAgenda(a.id)}
                >
                  Editar
                </Button>
                {a.id !== activePid && (
                  <Button
                    variant="secondary"
                    disabled={switching === a.id}
                    onClick={() => void switchTo(a.id)}
                  >
                    {switching === a.id ? "Cambiando…" : "Usar"}
                  </Button>
                )}
                {!a.isPrimary && (
                  <button
                    type="button"
                    disabled={deletingId === a.id}
                    onClick={() => void deleteAgenda(a.id)}
                    className={css({
                      px: "3",
                      py: "2",
                      rounded: "lg",
                      fontSize: "sm",
                      color: "#b91c1c",
                      borderWidth: "1px",
                      borderColor: "#fecaca",
                      bg: "#fef2f2",
                      cursor: deletingId === a.id ? "wait" : "pointer",
                      _hover: { bg: "#fee2e2" },
                    })}
                  >
                    {deletingId === a.id ? "Eliminando…" : "Eliminar"}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>

        {canCreate && (
          <Button variant="primary" onClick={() => setCreating(true)}>
            + Nueva agenda
          </Button>
        )}
      </div>
    </DashboardPageShell>
  );
}
